import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import UploadSection from './components/UploadSection';
import JobsTable from './components/JobsTable';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import { getTemporaryCredentials } from './utilities/CustomCredentialsProvider';
import { AnalyzerApiUrl } from './utilities/constants';
import './App.css';

export default function MainApp() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromURL = () => {
    const tab = new URLSearchParams(location.search).get('tab');
    return ['upload', 'jobs', 'analytics'].includes(tab) ? tab : 'upload';
  };

  const [currentPage, setCurrentPage] = useState(getTabFromURL);

  const handleTabChange = (_, val) => {
    setCurrentPage(val);
    navigate(`?tab=${val}`, { replace: true });
  };
  const [awsCredentials, setAwsCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [pageUsage, setPageUsage] = useState(null);

  const userEmail = auth?.user?.profile?.email ?? '';

  const idToken = auth?.user?.id_token ?? null;

  // Fetch temporary AWS credentials from Cognito Federated Identity
  const refreshCredentials = useCallback(async () => {
    try {
      const creds = await getTemporaryCredentials(auth.user.id_token);
      setAwsCredentials(creds);
    } catch (err) {
      console.error('Failed to get AWS credentials:', err);
    }
  }, [auth?.user?.id_token]);

  const fetchJobs = useCallback(async () => {
    if (!idToken) return;

    setLoading(true);

    try {
      const resp = await fetch(`${AnalyzerApiUrl}/jobs`, {
        headers: { Authorization: idToken },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.pageUsage) {
          setPageUsage(data.pageUsage);
        }
        const apiJobs = (data.jobs || []).map(job => {
          // Map backend status (uppercase) to frontend status (lowercase)
          let status = 'processing'; // Default for PENDING/PROCESSING
          if (job.status === 'COMPLETED') status = 'complete';
          if (job.status === 'FAILED') status = 'failed';

          return {
            ...job,
            submittedAt: job.created_at,
            completedAt: job.completed_at, // Ensure completedAt is mapped
            status
          };
        });

        setJobs(prevJobs => {
          // Create a map of API jobs by ID for easy lookup
          const apiJobMap = new Map(apiJobs.map(j => [j.requestId, j]));

          // Keep local jobs that are missing from API (handling GSI lag)
          // But if a local job exists in API, use the API version (source of truth)
          const merged = [];

          // 1. Add any local jobs that are NOT in the API yet (presumed newer)
          prevJobs.forEach(localJob => {
            if (!apiJobMap.has(localJob.requestId)) {
              merged.push(localJob);
            }
          });

          // 2. Add all API jobs
          merged.push(...apiJobs);

          // 3. Sort by submittedAt descending (just in case)
          merged.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

          return merged;
        });
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      refreshCredentials();
      // Refresh credentials every 45 minutes
      const id = setInterval(refreshCredentials, 45 * 60 * 1000);
      return () => clearInterval(id);
    }
  }, [auth.isAuthenticated, refreshCredentials]);

  useEffect(() => {
    if (currentPage === 'jobs') {
      fetchJobs();
    }
  }, [currentPage, fetchJobs]);

  const handleNavigateToJobs = () => setCurrentPage('jobs');

  React.useEffect(() => {
    const titles = {
      jobs: 'My Jobs | Remediation Analyzer',
      analytics: 'Analytics | Remediation Analyzer',
    };
    document.title = titles[currentPage] || 'Upload PDF | Remediation Analyzer';
  }, [currentPage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
      <Header />

      {/* Tab bar — sits just below the fixed header */}
      <Box
        sx={{
          mt: '64px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #e2e8f0',
          position: 'sticky',
          top: 64,
          zIndex: (t) => t.zIndex.appBar - 1,
        }}
      >
        <Tabs
          value={currentPage}
          onChange={handleTabChange}
          aria-label="Application navigation"
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            '& .MuiTabs-indicator': { backgroundColor: '#5F5BCD', height: 3 },
          }}
        >
          <Tab
            id="tab-upload"
            aria-controls="panel-upload"
            value="upload"
            label="Upload PDF"
            icon={<CloudUploadIcon />}
            iconPosition="start"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#64748b',
              '&.Mui-selected': { color: '#5F5BCD' },
              minHeight: 52,
            }}
          />
          <Tab
            id="tab-jobs"
            aria-controls="panel-jobs"
            value="jobs"
            label="My Jobs"
            icon={<FolderIcon />}
            iconPosition="start"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#64748b',
              '&.Mui-selected': { color: '#5F5BCD' },
              minHeight: 52,
            }}
          />
          <Tab
            id="tab-analytics"
            aria-controls="panel-analytics"
            value="analytics"
            label="Analytics"
            icon={<BarChartIcon />}
            iconPosition="start"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#64748b',
              '&.Mui-selected': { color: '#5F5BCD' },
              minHeight: 52,
            }}
          />
        </Tabs>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1100,
          mx: 'auto',
          width: '100%',
        }}
      >
        <div
          id="panel-upload"
          role="tabpanel"
          aria-labelledby="tab-upload"
          hidden={currentPage !== 'upload'}
        >
          {currentPage === 'upload' && (
            <UploadSection
              awsCredentials={awsCredentials}
              userEmail={userEmail}
              idToken={idToken}
              onJobsUpdate={setJobs}
              onNavigateToJobs={handleNavigateToJobs}
              externalPageUsage={pageUsage}
            />
          )}
        </div>
        <div
          id="panel-jobs"
          role="tabpanel"
          aria-labelledby="tab-jobs"
          hidden={currentPage !== 'jobs'}
        >
          {currentPage === 'jobs' && (
            <JobsTable
              jobs={jobs}
              onJobsUpdate={setJobs}
              idToken={idToken}
              loading={loading}
              onRefresh={fetchJobs}
            />
          )}
        </div>
        <div
          id="panel-analytics"
          role="tabpanel"
          aria-labelledby="tab-analytics"
          hidden={currentPage !== 'analytics'}
        >
          {currentPage === 'analytics' && (
            <AnalyticsDashboard idToken={idToken} />
          )}
        </div>
      </Box>

      <Footer />
    </Box>
  );
}
